/**
 * PÁGINA DE SIGNUP
 * 
 * Crear usuario en Supabase.
 * NO asigna productos, NO crea relaciones.
 * La asignación de acceso ocurre por invitación o desde producto.
 */

import React, { useState, FormEvent, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { getBrandingConfig } from '@shared/config/brandingConfig';
import { supabase } from '@shared/lib/supabaseClient';
import { verifyPhoneOTP, sendPhoneOTP } from '@shared/auth';
import { Button } from '@shared/ui/shadcn/ui/button';
import { Alert, AlertDescription } from '@shared/ui/shadcn/ui/alert';
import { Input } from '@shared/ui/shadcn/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@shared/ui/shadcn/ui/select';
import { Eye, EyeOff, RefreshCw } from 'lucide-react';
import AuthLayout from '../components/AuthLayout';
import { GoogleSignInButton } from '../components/GoogleSignInButton';
import { handleHandoffRedirect, setSSOCookie } from './Login';
import { createSsoTrace } from '@shared/utils/ssoTrace';
import { notifyCoreAPILogin } from '@shared/services/coreAuthService';
import { useDynamicBranding } from '../hooks/useDynamicBranding';
import { getTermsUrl, getPrivacyUrl } from '@shared/navigation';
import { APP_SSO_CONFIGS } from '@shared/navigation/appSSOConfig';
import { formatPhoneDisplay, normalizeToE164 } from '@shared/utils/phoneUtils';
import { UUID_REGEX } from '@shared/utils/validators';

const SignupPage: React.FC = () => {
  const [params] = useSearchParams();
  let app = params.get('app');
  const returnTo = params.get('returnTo');
  const source = params.get('source');
  const clientOriginParam = params.get('clientOrigin');

  // ✅ Inferir app desde parámetros (igual que Login) para redirigir al dashboard correcto
  if (!app || app === 'default') {
    if (source === 'thefomlabs') app = 'thefomlabs';
    else if (source === 'empresa') app = 'empresa';
    else if (source === 'houseme') app = 'houseme';
    else if (source === 'tienda') app = 'tienda';
    else if (returnTo?.includes('/empresa')) app = 'empresa';
    else if (returnTo?.includes('/thefomlabs')) app = 'thefomlabs';
    else if (returnTo?.includes('/houseme')) app = 'houseme';
    else if (returnTo?.includes('/tienda')) app = 'tienda';
    else if (returnTo?.includes('/map') || returnTo?.includes('/report') || returnTo?.includes('/alerts')) app = 'gobscore';
    else app = null;
  }

  // ✅ Aplicar branding dinámico (fallback a 'default' si app es desconocido)
  useDynamicBranding(app);
  const branding = getBrandingConfig(app);

  // Estados del formulario dividido en 2 pasos
  const [step, setStep] = useState<1 | 2>(1);
  const [fullName, setFullName] = useState('');
  const [country, setCountry] = useState('DO'); // Rep. Dom. por defecto
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // MEJORA: Nuevos campos para empresa y términos
  const [companyName, setCompanyName] = useState('');
  const [acceptTerms, setAcceptTerms] = useState(false);
  const trace = createSsoTrace({ app, feature: 'SSO_HANDOFF' });
  
  // Estados de foco para placeholders condicionales
  const [fullNameFocused, setFullNameFocused] = useState(false);
  const [phoneFocused, setPhoneFocused] = useState(false);
  const [emailFocused, setEmailFocused] = useState(false);
  const [passwordFocused, setPasswordFocused] = useState(false);
  const [confirmPasswordFocused, setConfirmPasswordFocused] = useState(false);
  
  // Estado para la contraseña recomendada
  const [recommendedPassword, setRecommendedPassword] = useState<string>('');

  // Estado híbrido: tras registro exitoso (sin sesión), mostrar pantalla OTP en la misma URL
  const [hybridCodesSent, setHybridCodesSent] = useState(false);
  const [otpInput, setOtpInput] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [editingPhone, setEditingPhone] = useState(false);
  const [editPhoneValue, setEditPhoneValue] = useState('');

  // Calcular el máximo de dígitos según el país
  const getMaxPhoneLength = () => {
    return country === 'DO' ? 10 : 15;
  };

  // Función para generar contraseña segura de 10 caracteres
  const generateSecurePassword = (): string => {
    // Caracteres permitidos: letras minúsculas, mayúsculas, números y símbolos
    const lowercase = 'abcdefghijklmnopqrstuvwxyz';
    const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const numbers = '0123456789';
    const symbols = '!@#$%&*';
    
    // Combinar todos los caracteres
    const allChars = lowercase + uppercase + numbers + symbols;
    
    // Asegurar al menos un carácter de cada tipo
    let password = '';
    password += lowercase[Math.floor(Math.random() * lowercase.length)];
    password += uppercase[Math.floor(Math.random() * uppercase.length)];
    password += numbers[Math.floor(Math.random() * numbers.length)];
    password += symbols[Math.floor(Math.random() * symbols.length)];
    
    // Completar hasta 10 caracteres con caracteres aleatorios
    for (let i = password.length; i < 10; i++) {
      password += allChars[Math.floor(Math.random() * allChars.length)];
    }
    
    // Mezclar los caracteres para que no estén siempre en el mismo orden
    return password.split('').sort(() => Math.random() - 0.5).join('');
  };

  // Handler para Paso 1: Nombre, País y Teléfono
  const handleStep1Submit = (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!fullName.trim()) {
      setError('El nombre completo es requerido');
      return;
    }
    if (!phone.trim()) {
      setError('El número de teléfono es requerido');
      return;
    }

    // Validar formato de teléfono (solo números)
    const phoneRegex = /^\+?[0-9]+$/;
    if (!phoneRegex.test(phone)) {
      setError('El teléfono solo debe contener números');
      return;
    }

    // Validar longitud según país
    const maxLength = getMaxPhoneLength();
    const phoneDigits = phone.replace(/\D/g, ''); // Solo dígitos para contar
    if (country === 'DO' && phoneDigits.length !== 10) {
      setError('El teléfono debe tener exactamente 10 dígitos para República Dominicana');
      return;
    }
    if (country !== 'DO' && phoneDigits.length > 15) {
      setError('El teléfono no puede tener más de 15 dígitos');
      return;
    }

    setStep(2);
  };

  // Handler para Paso 2: Email y Contraseña
  const handleStep2Submit = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    // Validación de email (solo si se proporcionó)
    if (email.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        setError('Por favor ingresa un email válido');
        setLoading(false);
        return;
      }
    }

    // Validación básica
    if (password !== confirmPassword) {
      setError('Las contraseñas no coinciden');
      setLoading(false);
      return;
    }

    // MEJORA: Validación de contraseña mejorada (8 caracteres mínimo)
    if (password.length < 8) {
      setError('La contraseña debe tener al menos 8 caracteres');
      setLoading(false);
      return;
    }
    
    // MEJORA: Validar que se aceptaron los términos
    if (!acceptTerms) {
      setError('Debes aceptar los términos y condiciones para continuar');
      setLoading(false);
      return;
    }

    try {
      // Si no hay email, generar uno temporal basado en el teléfono
      const finalEmail = email.trim() || `${phone.replace(/\D/g, '')}@temp.houseme.local`;
      
      console.log('[SIGNUP] Iniciando registro:', { finalEmail, hasEmail: !!email.trim() });
      
      // Crear una promesa con timeout de 15 segundos
      const signUpPromise = supabase.auth.signUp({
        email: finalEmail,
        password,
        options: {
          data: {
            sso_app: app,
            full_name: fullName,
            phone: phone,
            country: country,
            has_real_email: !!email.trim(),
            company_name: companyName.trim() || null, // MEJORA: Guardar nombre de empresa
          },
        },
      });

      // Timeout: signUp puede tardar si Supabase envía email de confirmación (SMTP lento) o hay latencia de red
      const SIGNUP_TIMEOUT_MS = 25000; // 25 s (antes 15 s) para reducir falsos timeouts
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => {
          reject(new Error('TIMEOUT: El proceso de registro está tardando demasiado. Esto puede deberse a problemas con el envío de email de confirmación. Por favor intenta de nuevo o verifica tu conexión.'));
        }, SIGNUP_TIMEOUT_MS);
      });

      let signUpResult;
      try {
        signUpResult = await Promise.race([signUpPromise, timeoutPromise]);
      } catch (timeoutError: any) {
        console.error('[SIGNUP] Timeout en signUp:', timeoutError);
        setError(
          timeoutError.message?.startsWith('TIMEOUT:')
            ? 'La respuesta del servidor tardó demasiado. Tu cuenta puede haberse creado igualmente: intenta iniciar sesión con tu email y contraseña, o revisa tu correo (y spam) por el enlace de confirmación. Si no puedes entrar, intenta registrarte de nuevo.'
            : (timeoutError.message || 'El proceso de registro está tardando demasiado. Por favor intenta de nuevo.')
        );
        setLoading(false);
        return;
      }

      const { data, error: signUpError } = signUpResult as { data: any; error: any };

      console.log('[SIGNUP] Respuesta de signUp:', { 
        hasData: !!data, 
        hasUser: !!data?.user, 
        hasSession: !!data?.session,
        userId: data?.user?.id,
        error: signUpError?.message 
      });

      if (signUpError) {
        console.error('[SIGNUP] Error en signUp:', signUpError);
        
        // Manejar errores específicos de timeout o email
        if (signUpError.message?.includes('timeout') || signUpError.message?.includes('504')) {
          setError('El proceso de registro está tardando demasiado debido a problemas con el envío de email. Tu cuenta puede haberse creado, pero necesitas verificar tu email. Revisa tu bandeja de entrada y spam.');
        } else {
          setError(signUpError.message);
        }
        setLoading(false);
        return;
      }

      // Verificar que la respuesta tiene datos válidos
      if (!data || (typeof data === 'object' && Object.keys(data).length === 0)) {
        console.error('[SIGNUP] Respuesta vacía o inválida:', data);
        setError('No se recibió respuesta del servidor. Esto puede deberse a un timeout. Tu cuenta puede haberse creado. Por favor verifica tu email o intenta iniciar sesión.');
        setLoading(false);
        return;
      }

      if (!data.user && !data.session) {
        console.error('[SIGNUP] Respuesta sin usuario ni sesión:', data);
        setError('No se pudo crear la cuenta. Por favor intenta de nuevo.');
        setLoading(false);
        return;
      }

      // Si el signup fue exitoso y hay sesión (auto-login)
      if (data.session) {
        trace.step('SIGNUP_SUCCESS_WITH_SESSION', {});

        const phoneE164 = normalizeToE164(phone) || phone.replace(/\D/g, '');
        const phoneForAuth = phoneE164 ? (phoneE164.startsWith('+') ? phoneE164 : `+${phoneE164}`) : null;

        // Referido Houseme: leer antes del upsert para vincular tras crear perfil
        const pendingReferral = typeof window !== 'undefined' ? localStorage.getItem('houseme_pending_referral') : null;

        // 1. Vinculación inmediata: asociar el teléfono al usuario auth ANTES de enviar el OTP.
        // Si el teléfono ya está en uso por otro usuario (reciclaje de números), no bloqueamos:
        // permitimos crear la cuenta con email y, al verificar el OTP, la Edge Function desvinculará
        // el número de la cuenta antigua y lo asignará a esta.
        if (phoneForAuth) {
          const { error: updateErr } = await supabase.auth.updateUser({ phone: phoneForAuth });
          if (updateErr) {
            console.warn('[SIGNUP] updateUser(phone) no pudo vincular (teléfono en uso o error). Se vinculará al verificar OTP:', updateErr.message);
          }
        }

        // 2. Una sola cuenta: crear/actualizar app_users con email + teléfono (1 user_id, 1 fila).
        await supabase.from('app_users').upsert({
          user_id: data.user.id,
          email: data.user.email ?? finalEmail,
          full_name: fullName,
          phone: phoneForAuth,
          status: 'activo',
          empresa_id: null,
          updated_at: new Date().toISOString(),
        }, { onConflict: 'user_id' });

        // 2b. Vínculo atómico de referido (Houseme): si había ?ref= guardado, actualizar referrer_user_id
        if (pendingReferral && UUID_REGEX.test(pendingReferral)) {
          try {
            const { error: refErr } = await supabase
              .from('app_users')
              .update({ referrer_user_id: pendingReferral })
              .eq('user_id', data.user.id);
            if (refErr) {
              console.warn('[SIGNUP] No se pudo vincular referido (auditoría):', refErr.message);
            } else if (typeof window !== 'undefined') {
              localStorage.removeItem('houseme_pending_referral');
            }
          } catch (refEx) {
            console.warn('[SIGNUP] Excepción al vincular referido:', refEx);
          }
        }

        // 3. OTP de verificación (no de login): enviar OTP en el contexto de "verificar teléfono de esta cuenta".
        // El usuario ya está autenticado; al verificar OTP pasamos email para que la Edge Function no cree otra identidad.
        if (phoneForAuth) {
          try {
            await sendPhoneOTP(phone.replace(/\D/g, '').trim());
          } catch (otpErr) {
            console.warn('[SIGNUP] Envío de OTP opcional:', otpErr);
          }
          setError(null);
          setMessage('Cuenta creada. Ingresa el código de 6 dígitos que enviamos a tu WhatsApp para verificar tu teléfono.');
          setHybridCodesSent(true);
          setResendCooldown(30);
          setLoading(false);
          return;
        }

        // Sin teléfono: notificar y hacer handoff directamente
        if (data.session.access_token) {
          await notifyCoreAPILogin(data.session.access_token);
        }
        const params = new URLSearchParams(window.location.search);
        const returnToParam = params.get('returnTo');
        const appParam = params.get('app') || app;
        if (appParam && appParam !== 'default') {
          const handoffSuccess = await handleHandoffRedirect(
            appParam,
            returnToParam,
            (errorMessage) => {
              setError(errorMessage);
              setLoading(false);
            },
            trace,
            clientOriginParam,
          );
          if (handoffSuccess) return;
        }
      }

      // Si no hay sesión (requiere verificación de email) o handoff falló
      if (data.user && !data.session) {
        // Usuario creado pero requiere verificación de email
        trace.step('SIGNUP_SUCCESS_REQUIRES_VERIFICATION', { userId: data.user.id });
        
        // Enviar email de confirmación personalizado por app (redirectTo en localhost se queda en localhost)
        try {
          const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
          if (supabaseUrl && data.user.email && data.user.id) {
            const functionUrl = `${supabaseUrl}/functions/v1/send-confirmation-email`;
            const returnToParam = new URLSearchParams(window.location.search).get('returnTo');
            // Mantener flujo en localhost: no usar URLs de producción cuando estamos en localhost
            const origin = window.location.origin;
            const redirectTo = returnToParam
              ? decodeURIComponent(returnToParam)
              : `${origin}/auth/callback`;

            // Llamar al Edge Function para enviar email personalizado
            // El Edge Function generará el link de confirmación usando Admin API
            const response = await fetch(functionUrl, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
              },
              body: JSON.stringify({
                email: data.user.email,
                userId: data.user.id,
                appName: app,
                userName: fullName,
                redirectTo: redirectTo,
              }),
            });
            
            if (!response.ok) {
              const errorData = await response.json().catch(() => ({}));
              console.warn('[SIGNUP] No se pudo enviar email personalizado:', errorData);
              // Supabase enviará el email default si está configurado
            } else {
              const result = await response.json();
              console.log('[SIGNUP] Email personalizado enviado exitosamente:', result);
            }
          }
        } catch (emailError) {
          console.warn('[SIGNUP] Error al enviar email personalizado:', emailError);
          // No fallar el registro si el email falla - Supabase enviará el default
        }

        // Enviar OTP al teléfono para verificación en la misma pantalla (sin redirigir)
        const phoneDigits = phone.replace(/\D/g, '').trim();
        if (phoneDigits) {
          sendPhoneOTP(phoneDigits).catch((err) => console.warn('[SIGNUP] OTP por teléfono opcional:', err));
        }

        setError(null);
        setMessage('Cuenta creada. Revisa tu correo para el enlace o ingresa el código de 6 dígitos que enviamos a tu WhatsApp.');
        setHybridCodesSent(true);
        setResendCooldown(30);
        setLoading(false);
        return;
      }

      // Fallback: si llegamos aquí sin sesión ni usuario, algo salió mal
      setError('No se pudo completar el registro. Por favor intenta de nuevo.');
      setLoading(false);
    } catch (err) {
      setError('Error inesperado. Por favor intenta de nuevo.');
      setLoading(false);
    }
  };

  const handleBack = () => {
    setStep(1);
    setError(null);
  };

  // Verificar OTP tras registro exitoso (misma pantalla, sin redirección). Pasar email para actualizar la misma cuenta y no crear una segunda.
  const handleVerifySignupOTP = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    const phoneDigits = phone.replace(/\D/g, '').trim();
    if (otpInput.length !== 6) {
      setError('El código debe tener 6 dígitos');
      return;
    }
    const appParam = params.get('app') || app;
    if (!appParam || !APP_SSO_CONFIGS[appParam]) {
      setError('App no soportada');
      return;
    }
    setLoading(true);
    const result = await verifyPhoneOTP(phoneDigits, otpInput, email.trim() || undefined);
    if (!result.success) {
      setError(result.error || 'Código inválido');
      setLoading(false);
      return;
    }
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.user?.id) {
      const phoneE164 = normalizeToE164(phoneDigits) || phoneDigits;
      if (phoneE164) {
        await supabase.from('app_users').update({ phone: phoneE164 }).eq('user_id', session.user.id);
      }
    }
    if (session?.access_token) {
      try {
        await notifyCoreAPILogin(session.access_token);
        await setSSOCookie(session.access_token);
      } catch (_) {}
    }
    const returnToParam = params.get('returnTo');
    const ok = await handleHandoffRedirect(
      appParam,
      returnToParam,
      (msg) => setError(msg),
      trace,
      clientOriginParam,
    );
    if (!ok) setLoading(false);
  };

  // Cuenta atrás 30 s para reenviar código OTP (Signup)
  useEffect(() => {
    if (!hybridCodesSent || resendCooldown <= 0) return;
    const t = setInterval(() => setResendCooldown((c) => (c <= 1 ? 0 : c - 1)), 1000);
    return () => clearInterval(t);
  }, [hybridCodesSent, resendCooldown]);

  const handleResendSignupOTP = async () => {
    if (resendCooldown > 0 || loading) return;
    const phoneDigits = phone.replace(/\D/g, '').trim();
    if (!phoneDigits) return;
    setError(null);
    setLoading(true);
    const result = await sendPhoneOTP(phoneDigits);
    setLoading(false);
    if (result.success) {
      setResendCooldown(30);
      setMessage('Código reenviado. Revisa tu WhatsApp.');
      setTimeout(() => setMessage(null), 5000);
    } else {
      setError(result.error || 'No se pudo reenviar el código.');
    }
  };

  // Función para usar la contraseña recomendada
  const handleUseRecommendedPassword = () => {
    const newPassword = generateSecurePassword();
    setRecommendedPassword(newPassword);
    setPassword(newPassword);
    setConfirmPassword(newPassword);
    setShowPassword(true); // Mostrar la contraseña para que el usuario la vea
    setShowConfirmPassword(true);
  };

  return (
    <AuthLayout>
      {/* ✅ DISEÑO MODERNO - Consistente con Login */}
      <div className="w-full flex flex-col gap-6">
        {/* Logo + Título */}
        <div className="text-center space-y-3">
          {app === 'houseme' ? (
            <div className="flex items-center justify-center gap-2">
              <img src={branding.assets.logo || "/icon-houseme-192x192.png"} alt="HouseMe" className="h-8 w-8" />
              <span className="text-lg lg:text-xl font-bold">
                <span style={{ color: `hsl(${branding.colors.primary})` }}>House</span>
                <span style={{ color: `hsl(${branding.colors.secondary || branding.colors.primary})` }}>Me</span>
              </span>
            </div>
          ) : app === 'empresa' ? (
            <div className="flex items-center justify-center gap-2">
              <img 
                src={branding.assets.logo || "/icon-192x192.png"} 
                alt="ThePlatform360" 
                className="h-8 w-8"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  if (target.src !== window.location.origin + '/icon-192x192.png') {
                    target.src = '/icon-192x192.png';
                  }
                }}
              />
              <span className="text-lg lg:text-xl font-bold" style={{ color: `hsl(${branding.colors.primary})` }}>
                ThePlatform360
              </span>
            </div>
          ) : branding.assets.logo ? (
            <img src={branding.assets.logo} alt={branding.label} className="h-12 mx-auto" />
          ) : null}
          
          <div>
            <h1 className="text-xl font-semibold text-foreground">Crear cuenta</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Únete a {branding.label}
            </p>
          </div>
        </div>

        {/* Pantalla OTP: tras registro exitoso, misma URL sin redirigir */}
        {hybridCodesSent ? (
          <div className="space-y-4">
            {(error || message) && (
              <Alert variant={error ? 'destructive' : 'default'} className={error ? '' : 'bg-green-50 border-green-200 text-green-800'}>
                <AlertDescription>{error || message}</AlertDescription>
              </Alert>
            )}
            <p className="text-sm text-muted-foreground">
              Ingresa el código de 6 dígitos que enviamos a tu WhatsApp (o revisa tu correo para el enlace).
            </p>
            {!editingPhone ? (
              <div className="flex items-center justify-between gap-2 rounded-md bg-muted/50 px-3 py-2 text-sm">
                <span className="text-muted-foreground">Enviado a</span>
                <span className="font-medium tabular-nums">
                  {formatPhoneDisplay(normalizeToE164(phone) || phone) || phone || '—'}
                </span>
                <button
                  type="button"
                  onClick={() => { setEditingPhone(true); setEditPhoneValue(phone.replace(/\D/g, '')); setError(null); setMessage(null); }}
                  className="text-primary hover:underline font-medium text-xs shrink-0"
                >
                  Editar número
                </button>
              </div>
            ) : (
              <div className="rounded-md border bg-muted/30 p-3 space-y-2">
                <label className="text-xs font-medium text-muted-foreground">Nuevo número</label>
                <div className="flex gap-2">
                  <Input
                    type="tel"
                    inputMode="numeric"
                    placeholder="Ej: 8286902481"
                    value={editPhoneValue}
                    onChange={(e) => setEditPhoneValue(e.target.value.replace(/\D/g, '').slice(0, 15))}
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    size="sm"
                    disabled={!editPhoneValue.trim() || editPhoneValue.length < 10 || loading}
                    onClick={async () => {
                      const normalized = normalizeToE164(editPhoneValue) || editPhoneValue.trim();
                      if (!normalized) return;
                      setError(null);
                      setLoading(true);
                      const result = await sendPhoneOTP(normalized);
                      setLoading(false);
                      if (result.success) {
                        setPhone(normalized);
                        setEditingPhone(false);
                        setEditPhoneValue('');
                        setResendCooldown(30);
                        setOtpInput('');
                        setMessage('Código enviado al nuevo número. Revisa tu WhatsApp.');
                        setTimeout(() => setMessage(null), 5000);
                      } else {
                        setError(result.error || 'No se pudo enviar el código.');
                      }
                    }}
                  >
                    Enviar a este número
                  </Button>
                </div>
                <button
                  type="button"
                  onClick={() => { setEditingPhone(false); setEditPhoneValue(''); }}
                  className="text-xs text-muted-foreground hover:underline"
                >
                  Cancelar
                </button>
              </div>
            )}
            <form
              onSubmit={handleVerifySignupOTP}
              className="space-y-4"
            >
              <div className="space-y-1.5">
                <Input
                  id="signup-otp"
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  label="Código de 6 dígitos"
                  value={otpInput}
                  onChange={(e) => setOtpInput(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  disabled={loading}
                  className="placeholder:text-muted-foreground/40"
                />
              </div>
              <Button
                type="submit"
                disabled={otpInput.length !== 6 || loading}
                className="w-full"
                size="lg"
              >
                {loading ? 'Verificando...' : 'Verificar código'}
              </Button>
              <p className="text-center text-xs text-muted-foreground">
                {resendCooldown > 0 ? (
                  <>Reenviar código en <span className="font-medium tabular-nums">{resendCooldown}</span> s</>
                ) : (
                  <button
                    type="button"
                    onClick={handleResendSignupOTP}
                    disabled={loading}
                    className="text-primary hover:underline font-medium disabled:opacity-50"
                  >
                    Reenviar código
                  </button>
                )}
              </p>
            </form>
          </div>
        ) : (
          <>
        {/* Mismo botón que en Login: Continuar con Google + enlace Iniciar sesión */}
        <GoogleSignInButton
          returnTo={returnTo ?? undefined}
          app={app ?? undefined}
          className="w-full mb-2"
          onSignInStart={() => setLoading(true)}
          onError={(err) => {
            setLoading(false);
            setError(err?.message ?? 'Error al iniciar sesión con Google');
          }}
        />
        <div className="flex items-center gap-2 my-2">
          <div className="flex-1 h-px bg-border" />
          <span className="text-xs text-muted-foreground">o</span>
          <div className="flex-1 h-px bg-border" />
        </div>
        <Link
          to={`/login?app=${app}${returnTo ? `&returnTo=${encodeURIComponent(returnTo)}` : ''}`}
          className="block w-full mb-2"
        >
          <Button type="button" variant="outline" className="w-full" size="lg">
            Ya tengo cuenta · Iniciar sesión
          </Button>
        </Link>
        <div className="flex items-center gap-2 my-2">
          <div className="flex-1 h-px bg-border" />
          <span className="text-xs text-muted-foreground">Crear cuenta con email</span>
          <div className="flex-1 h-px bg-border" />
        </div>

        {/* Formulario Paso 1: Nombre, País y Teléfono */}
        {step === 1 && (
          <form onSubmit={handleStep1Submit} className="space-y-3">
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-1">
              <Input
                id="fullName"
                type="text"
                label="Nombre completo"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                onFocus={() => setFullNameFocused(true)}
                onBlur={() => setFullNameFocused(false)}
                required
                autoComplete="name"
                placeholder={fullNameFocused ? "Escríbelo aquí" : ""}
                className="placeholder:text-muted-foreground/40"
              />
            </div>

            <div className="space-y-1">
              <Input
                id="phone"
                type="tel"
                inputMode="numeric"
                label={`Número de teléfono ${country === 'DO' ? '(10 dígitos)' : '(máx. 15 dígitos)'}`}
                value={phone}
                onChange={(e) => {
                  const value = e.target.value;
                  // Solo permitir números y opcionalmente un '+' al inicio
                  if (/^\+?[0-9]*$/.test(value)) {
                    const maxLength = getMaxPhoneLength();
                    // Contar solo dígitos para el límite
                    const digits = value.replace(/\D/g, '');
                    if (digits.length <= maxLength) {
                      setPhone(value);
                    }
                  }
                }}
                onFocus={() => setPhoneFocused(true)}
                onBlur={() => setPhoneFocused(false)}
                required
                autoComplete="tel"
                placeholder={phoneFocused ? "Escríbelo aquí" : ""}
                className="placeholder:text-muted-foreground/40"
              />
              <p className="text-xs text-muted-foreground">
                {country === 'DO' 
                  ? 'Ejemplo: 8095551234 (sin guiones ni espacios)' 
                  : 'Incluye código de país si es necesario (ej: +1234567890)'}
              </p>
            </div>

            {/* Selector de país en texto */}
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <span>País:</span>
              <Select value={country} onValueChange={setCountry}>
                <SelectTrigger className="h-7 w-auto border-0 bg-transparent px-2 text-xs font-medium text-foreground hover:bg-muted/50 focus:ring-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="DO">🇩🇴 República Dominicana</SelectItem>
                  <SelectItem value="US">🇺🇸 Estados Unidos</SelectItem>
                  <SelectItem value="ES">🇪🇸 España</SelectItem>
                  <SelectItem value="MX">🇲🇽 México</SelectItem>
                  <SelectItem value="CO">🇨🇴 Colombia</SelectItem>
                  <SelectItem value="VE">🇻🇪 Venezuela</SelectItem>
                  <SelectItem value="AR">🇦🇷 Argentina</SelectItem>
                  <SelectItem value="CL">🇨🇱 Chile</SelectItem>
                  <SelectItem value="PE">🇵🇪 Perú</SelectItem>
                  <SelectItem value="EC">🇪🇨 Ecuador</SelectItem>
                  <SelectItem value="GT">🇬🇹 Guatemala</SelectItem>
                  <SelectItem value="CU">🇨🇺 Cuba</SelectItem>
                  <SelectItem value="BO">🇧🇴 Bolivia</SelectItem>
                  <SelectItem value="HT">🇭🇹 Haití</SelectItem>
                  <SelectItem value="HN">🇭🇳 Honduras</SelectItem>
                  <SelectItem value="PY">🇵🇾 Paraguay</SelectItem>
                  <SelectItem value="SV">🇸🇻 El Salvador</SelectItem>
                  <SelectItem value="NI">🇳🇮 Nicaragua</SelectItem>
                  <SelectItem value="CR">🇨🇷 Costa Rica</SelectItem>
                  <SelectItem value="PA">🇵🇦 Panamá</SelectItem>
                  <SelectItem value="UY">🇺🇾 Uruguay</SelectItem>
                  <SelectItem value="PR">🇵🇷 Puerto Rico</SelectItem>
                  <SelectItem value="OTHER">🌎 Otro país</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Button type="submit" className="w-full" size="lg">
              Continuar
            </Button>
          </form>
        )}

        {/* Formulario Paso 2: Email y Contraseña */}
        {step === 2 && (
          <form onSubmit={handleStep2Submit} className="space-y-3">
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-1">
              <Input
                id="email"
                type="email"
                label="Email (recomendado para mayor seguridad)"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onFocus={() => setEmailFocused(true)}
                onBlur={() => setEmailFocused(false)}
                autoComplete="email"
                placeholder={emailFocused ? "Escríbelo aquí" : ""}
                className="placeholder:text-muted-foreground/40"
              />
            </div>

            <div className="space-y-1">
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  label="Contraseña"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onFocus={() => setPasswordFocused(true)}
                  onBlur={() => setPasswordFocused(false)}
                  required
                  autoComplete="new-password"
                  placeholder={passwordFocused ? "Escríbela aquí" : ""}
                  className="pr-10 placeholder:text-muted-foreground/40"
                  minLength={8}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              
              {/* Recomendación de contraseña segura */}
              <div className="flex flex-col gap-1 mt-1">
                <button
                  type="button"
                  onClick={handleUseRecommendedPassword}
                  className="text-xs text-primary hover:text-primary/80 hover:underline flex items-center gap-1 transition-colors w-fit"
                >
                  <RefreshCw className="h-3 w-3" />
                  Usar contraseña segura recomendada (10 caracteres)
                </button>
                
                {recommendedPassword && (
                  <p className="text-xs text-muted-foreground">
                    ✓ Contraseña generada: <span className="font-mono font-medium">{recommendedPassword}</span>
                  </p>
                )}
                
                <p className="text-xs text-muted-foreground mt-1">
                  💡 Recomendación: Usa una contraseña de al menos 10 caracteres con letras, números y símbolos
                </p>
              </div>
            </div>

            <div className="space-y-1">
              <div className="relative">
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  label="Confirmar Contraseña"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  onFocus={() => setConfirmPasswordFocused(true)}
                  onBlur={() => setConfirmPasswordFocused(false)}
                  required
                  autoComplete="new-password"
                  placeholder={confirmPasswordFocused ? "Escríbela aquí" : ""}
                  className="pr-10 placeholder:text-muted-foreground/40"
                  minLength={8}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  tabIndex={-1}
                >
                  {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              
              {/* Alerta en tiempo real si las contraseñas no coinciden */}
              {confirmPassword.length > 0 && !password.startsWith(confirmPassword) && (
                <Alert variant="destructive" className="py-2">
                  <AlertDescription className="text-xs">
                    Las contraseñas no coinciden
                  </AlertDescription>
                </Alert>
              )}
            </div>

            {/* MEJORA: Campo opcional de nombre de empresa (solo para app "empresa") */}
            {app === 'empresa' && (
              <div className="space-y-1">
                <Input
                  id="companyName"
                  type="text"
                  label="Nombre de tu empresa (opcional)"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  placeholder="Ej: Mi Empresa S.A."
                  className="placeholder:text-muted-foreground/40"
                />
                <p className="text-xs text-muted-foreground">
                  Si lo completas, crearemos tu empresa automáticamente
                </p>
              </div>
            )}

            {/* MEJORA: Checkbox de términos y condiciones */}
            <div className="flex items-start gap-2 py-2">
              <input
                type="checkbox"
                id="acceptTerms"
                checked={acceptTerms}
                onChange={(e) => setAcceptTerms(e.target.checked)}
                required
                className="mt-1"
              />
              <label htmlFor="acceptTerms" className="text-xs text-muted-foreground cursor-pointer">
                Al crear una cuenta aceptas nuestros{' '}
                <a
                  href={getTermsUrl(app || undefined)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline"
                >
                  Términos de Servicio
                </a>
                {' '}y{' '}
                <a
                  href={getPrivacyUrl()}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline"
                >
                  Política de Privacidad
                </a>
              </label>
            </div>

            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={handleBack} className="w-1/3">
                Volver
              </Button>
              <Button type="submit" disabled={loading || !acceptTerms} className="w-2/3" size="lg">
                {loading ? 'Creando cuenta...' : 'Crear cuenta'}
              </Button>
            </div>
          </form>
        )}
          </>
        )}

        {/* Footer compacto */}
        <div className="mt-4 text-center text-xs text-muted-foreground">
          ¿Ya tienes cuenta?{' '}
          <a
            href={`/login?app=${app}${returnTo ? `&returnTo=${encodeURIComponent(returnTo)}` : ''}`}
            className="text-primary hover:underline font-medium"
          >
            Inicia sesión
          </a>
        </div>
      </div>
    </AuthLayout>
  );
};

export default SignupPage;
